import { Chip } from '@mui/material';
import type { InvoiceOverview, MarketplaceSyncStatus } from '@vy/api-definition';
import { JOB_TASK_STATUSES } from '@vy/jobs-types';
import React from 'react';
import { VyTooltip } from '../../../../../../components/new-design/VyTooltip/VyTooltip';
import { notDefined, whenDefined } from '@vy/null';
import { MARKETPLACE_SYNC_COLORS } from './marketplace-sync-colors';

type MarketplaceSyncStatusCellProps = {
  row: InvoiceOverview;
};

export const MarketplaceSyncStatusCell: React.FC<MarketplaceSyncStatusCellProps> = ({ row }) => {
  const syncStatus = row.marketplaceSyncStatus;

  if (notDefined(syncStatus)) {
    return <span>—</span>;
  }

  const { label, color, backgroundColor, tooltipContent } = getStatusDisplay(syncStatus);

  return (
    <VyTooltip placement="top" tooltipContent={tooltipContent}>
      <Chip
        label={label}
        sx={{
          backgroundColor,
          color,
          fontWeight: 500,
          fontSize: '0.875rem',
        }}
      />
    </VyTooltip>
  );
};

type StatusDisplay = {
  label: string;
  color: string;
  backgroundColor: string;
  tooltipContent: React.ReactNode;
};

function getStatusDisplay(syncStatus: MarketplaceSyncStatus): StatusDisplay {
  switch (syncStatus.status) {
    case JOB_TASK_STATUSES.Completed:
      return {
        label: 'Synced',
        color: MARKETPLACE_SYNC_COLORS.completed.text,
        backgroundColor: MARKETPLACE_SYNC_COLORS.completed.background,
        tooltipContent: whenDefined(syncStatus.message, (message) => (
          <span style={{ whiteSpace: 'pre-line' }}>{message}</span>
        )),
      };
    case JOB_TASK_STATUSES.Failed:
      return {
        label: 'Failed',
        color: MARKETPLACE_SYNC_COLORS.failed.text,
        backgroundColor: MARKETPLACE_SYNC_COLORS.failed.background,
        tooltipContent: syncStatus.message ?? 'Sync failed',
      };
    case JOB_TASK_STATUSES.InProgress:
    case JOB_TASK_STATUSES.Pending:
      return {
        label: 'In Progress',
        color: MARKETPLACE_SYNC_COLORS.inProgress.text,
        backgroundColor: MARKETPLACE_SYNC_COLORS.inProgress.background,
        tooltipContent: undefined,
      };
    default:
      return {
        label: 'Unknown',
        color: MARKETPLACE_SYNC_COLORS.unknown.text,
        backgroundColor: MARKETPLACE_SYNC_COLORS.unknown.background,
        tooltipContent: undefined,
      };
  }
}
